'use client';

import { Form, FormSubmitButton, Skeleton } from '@baseball-near-you/ui';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useUpdateUserMutation } from '@bb/lib/mutations/useUpdateUserMutation';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { LeftRightInputs } from '../LeftRightInputs';

export enum Handedness {
  Right = 'Right',
  Left = 'Left',
  Switch = 'Switch',
}

export function PersonalUserHandednessForm(props: {
  onSuccess?: () => Promise<void>;
  submitButtonLabel?: string;
}) {
  const { userProfile } = useUserProfile();
  const { updateUser } = useUpdateUserMutation();

  return userProfile ? (
    <Form
      onSubmit={async ({ batHandedness, throwHandedness }) => {
        if (!userProfile?.id) {
          return;
        }
        await updateUser({
          id: userProfile.id,
          batHandedness: batHandedness ?? undefined,
          throwHandedness: throwHandedness ?? undefined,
        });
        await props.onSuccess?.();
      }}
      schema={z.object({
        throwHandedness: z.nativeEnum(Handedness).nullable(),
        batHandedness: z.nativeEnum(Handedness).nullable(),
      })}
      initialValues={{
        throwHandedness: userProfile?.throwHandedness ?? null,
        batHandedness: userProfile?.batHandedness ?? null,
      }}
    >
      <HandednessInputs />
      <FormSubmitButton
        className="mt-4"
        label={props.submitButtonLabel ?? 'Update'}
      />
    </Form>
  ) : (
    <Skeleton />
  );
}

export function HandednessInputs() {
  const { setValue, watch, getValues } = useFormContext();

  const throwHandedness = watch('throwHandedness');
  const batHandedness = watch('batHandedness');

  return (
    <>
      <LeftRightInputs
        label="I throw..."
        value={throwHandedness}
        onChange={(value) => {
          setValue('throwHandedness', value);
        }}
        className="mt-4"
      />
      <LeftRightInputs
        label="I bat..."
        value={batHandedness}
        onChange={(value) => {
          setValue('batHandedness', value);
        }}
        className="mt-4"
        hint="Switch hitters should select both."
      />
    </>
  );
}
